import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../../components/common/pageLayout';
import Configure from '../../components/MailBox/configure';
import { v4_mailbox_configuration, v4_mailbox_configure, v4_mailbox_logout } from '../../actions/v4/mailBox';
import { getSessionStorage, removeStorage, setSessionStorage } from '../../utils';
import constant from '../../constant';
import MailboxAccessPanel from './mailboxAccessPanel';
import './mailboxSettings.css';

const providerNames = {
    microsoft: 'Microsoft 365',
    gmail: 'Gmail',
    yahoo: 'Yahoo Mail',
    outlook: 'Outlook',
    hotmail: 'Hotmail',
    other: 'Custom server',
};

const providerName = (host) => providerNames[String(host || '').toLowerCase()] || 'Email account';
const providerInitial = (host) => String(providerName(host)).charAt(0).toUpperCase();
const errorText = (error, fallback) => error?.response?.data?.error || error?.message || fallback;

const Index = () => {
    const [mailboxes, setMailboxes] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [view, setView] = useState('details');
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(false);
    const [listError, setListError] = useState('');
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [accessSaving, setAccessSaving] = useState(false);

    const loadMailboxes = useCallback(async (preferredEmail = '') => {
        const response = await v4_mailbox_configuration();
        if (!response.status) throw new Error(response.error || 'Could not load mailboxes.');
        const accounts = response.result || [];
        setMailboxes(accounts);
        setSelectedId((previous) => {
            const newlyAdded = preferredEmail && accounts.find((item) =>
                String(item.EMAIL).toLowerCase() === String(preferredEmail).toLowerCase());
            if (newlyAdded) return String(newlyAdded.ID);
            if (accounts.some((item) => String(item.ID) === String(previous))) return String(previous);
            const remembered = getSessionStorage(constant.keys.mailBox);
            if (remembered && accounts.some((item) => String(item.ID) === String(remembered))) return String(remembered);
            return accounts.length ? String(accounts[0].ID) : '';
        });
        setListError('');
        return accounts;
    }, []);

    useEffect(() => {
        loadMailboxes().catch((error) => setListError(errorText(error, 'Could not load mailboxes.')))
            .finally(() => setLoading(false));
    }, [loadMailboxes]);

    const selectedMailbox = mailboxes.find((item) => String(item.ID) === String(selectedId)) || mailboxes[0];
    const ownedCount = mailboxes.filter((item) => Number(item.IS_OWNER)).length;
    const filteredMailboxes = useMemo(() => {
        const term = search.trim().toLowerCase();
        return mailboxes.filter((item) => {
            if (filter === 'owned' && !Number(item.IS_OWNER)) return false;
            if (filter === 'other' && Number(item.IS_OWNER)) return false;
            return !term || `${item.EMAIL} ${item.OWNER_NAME || ''} ${providerName(item.HOST)}`
                .toLowerCase().includes(term);
        });
    }, [mailboxes, filter, search]);

    const confirmDiscard = () => {
        if (accessSaving) {
            toast.info('Please wait for access changes to finish saving.');
            return false;
        }
        return !hasUnsavedChanges || window.confirm(
            'You have unsaved access changes. Discard them and switch mailboxes?'
        );
    };

    const selectMailbox = (mailbox) => {
        if (view === 'details' && String(mailbox.ID) === String(selectedMailbox?.ID)) return;
        if (!confirmDiscard()) return;
        setHasUnsavedChanges(false);
        setSelectedId(String(mailbox.ID));
        setView('details');
    };

    const showAddForm = () => {
        if (!confirmDiscard()) return;
        setHasUnsavedChanges(false);
        setView('add');
    };

    const onConfigure = async (payload) => {
        setWorking(true);
        try {
            const response = await v4_mailbox_configure(payload);
            if (!response.status) throw new Error(response.error || 'Could not connect mailbox.');
            removeStorage(constant.keys.mailFolder);
            await loadMailboxes(payload.email);
            setView('details');
            toast.success(response.result?.message || 'Mailbox connected successfully.');
        } catch (error) {
            toast.error(errorText(error, 'Could not connect mailbox.'));
        } finally {
            setWorking(false);
        }
    };

    const onRemove = async (mailbox) => {
        if (accessSaving) return;
        if (!window.confirm(`Remove ${mailbox.EMAIL}? This disconnects all saved connections for this address and removes assigned access.`)) return;
        setWorking(true);
        try {
            const response = await v4_mailbox_logout(mailbox.ID);
            if (!response.status) throw new Error(response.error || 'Could not remove mailbox.');
            await loadMailboxes();
            setHasUnsavedChanges(false);
            toast.success(`${mailbox.EMAIL} was removed.`);
        } catch (error) {
            toast.error(errorText(error, 'Could not remove mailbox.'));
        } finally {
            setWorking(false);
        }
    };

    const onMicrosoftSSO = () => {
        const azureClientId = process.env.REACT_APP_AZURE_CLIENT_ID;
        const azureTenantId = process.env.REACT_APP_AZURE_TENANT_ID;
        if (!azureClientId || !azureTenantId) {
            toast.error('Microsoft SSO is not configured. Missing Azure client or tenant ID.');
            return;
        }
        const redirectUri = `${window.location.origin}/auth/callback`;
        const scope = 'openid profile email offline_access User.Read Mail.ReadWrite Mail.Send';
        window.location.href = `https://login.microsoftonline.com/${azureTenantId}/oauth2/v2.0/authorize`
            + `?client_id=${azureClientId}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}`
            + `&response_mode=query&prompt=select_account&scope=${encodeURIComponent(scope)}`;
    };

    const onSavedAccess = (count) => {
        setMailboxes((current) => current.map((item) => String(item.ID) === String(selectedMailbox?.ID)
            ? { ...item, GRANTED_USER_COUNT: count } : item));
    };

    return <Layout title="Mail settings" nonCardVersion={true}>
        <div className="mail-settings-page">
            <header className="mail-settings-header">
                <div>
                    <h1>Connected mailboxes</h1>
                    <p>Manage connected accounts and choose who can use each mailbox.</p>
                </div>
                <button type="button" className="mail-settings-primary-button mail-settings-add-button"
                    onClick={showAddForm} disabled={working || accessSaving || view === 'add'}>
                    <span aria-hidden="true">+</span> Add mailbox
                </button>
            </header>

            <div className="mail-settings-summary" aria-label="Mailbox summary">
                <span><strong>{mailboxes.length}</strong> connected {mailboxes.length === 1 ? 'mailbox' : 'mailboxes'}</span>
                <span className="mail-settings-summary-divider" aria-hidden="true" />
                <span><strong>{ownedCount}</strong> owned by you</span>
                <span className="mail-settings-summary-divider" aria-hidden="true" />
                <span><strong>{mailboxes.length - ownedCount}</strong> available through access</span>
            </div>

            <div className="mail-settings-layout">
                <aside className="mail-settings-list-panel" aria-label="Connected mailboxes">
                    <div className="mail-settings-list-header">
                        <h2>Mailboxes</h2>
                        <span>{mailboxes.length}</span>
                    </div>
                    <div className="mail-settings-search-box mail-settings-mailbox-search">
                        <i className="bx bx-search" aria-hidden="true" />
                        <input type="search" aria-label="Search mailboxes" placeholder="Search mailboxes"
                            value={search} onChange={(event) => setSearch(event.target.value)} />
                    </div>
                    <div className="mail-settings-filters" role="group" aria-label="Filter mailboxes">
                        {[
                            ['all', 'All'], ['owned', 'Owned'], ['other', 'Other access'],
                        ].map(([key, label]) => <button key={key} type="button"
                            className={filter === key ? 'active' : ''}
                            aria-pressed={filter === key} onClick={() => setFilter(key)}>{label}</button>)}
                    </div>

                    {loading ? <div className="mail-settings-list-state">Loading mailboxes...</div> :
                        listError ? <div className="mail-settings-list-state mail-settings-state-error">
                            <p>{listError}</p>
                            <button type="button" onClick={() => loadMailboxes().catch((error) =>
                                setListError(errorText(error, 'Could not load mailboxes.')))}>Try again</button>
                        </div> : filteredMailboxes.length ? <div className="mail-settings-mailbox-list">
                            {filteredMailboxes.map((mailbox) => <button type="button" key={mailbox.ID}
                                className={`mail-settings-mailbox-row ${view === 'details' && String(selectedMailbox?.ID) === String(mailbox.ID) ? 'active' : ''}`}
                                aria-current={view === 'details' && String(selectedMailbox?.ID) === String(mailbox.ID) ? 'true' : undefined}
                                onClick={() => selectMailbox(mailbox)}>
                                <span className={`mail-settings-provider-icon mail-settings-provider-${String(mailbox.HOST || 'other').toLowerCase()}`}>
                                    {providerInitial(mailbox.HOST)}
                                </span>
                                <span className="mail-settings-mailbox-row-content">
                                    <strong>{mailbox.EMAIL}</strong>
                                    <small>{providerName(mailbox.HOST)} · {Number(mailbox.IS_OWNER) ? 'Owned by you' : mailbox.OWNER_NAME || 'Shared account'}</small>
                                    <span className="mail-settings-mailbox-row-meta">
                                        <span className="mail-settings-role-tag">{Number(mailbox.IS_OWNER) ? 'Owner' : Number(mailbox.IS_ADMIN) ? 'Admin' : 'Shared'}</span>
                                        {Number(mailbox.CAN_MANAGE_ACCESS) ? <span>{Number(mailbox.GRANTED_USER_COUNT) || 0} assigned</span> : null}
                                    </span>
                                </span>
                                <i className="bx bx-chevron-right" aria-hidden="true" />
                            </button>)}
                        </div> : <div className="mail-settings-list-state">
                            {mailboxes.length ? 'No mailboxes match your search.' : 'No mailboxes available to you.'}
                        </div>}
                </aside>

                <main className="mail-settings-detail-panel">
                    {view === 'add' ? <div className="mail-settings-add-view">
                        <button type="button" className="mail-settings-back-button" onClick={() => setView('details')}>
                            <i className="bx bx-arrow-back" aria-hidden="true" /> Back to mailboxes
                        </button>
                        <h2>Connect a mailbox</h2>
                        <p>Choose your email provider and enter its connection details.</p>
                        <div className="mail-settings-add-form">
                            <Configure onSubmit={onConfigure} onMicrosoftSSO={onMicrosoftSSO}
                                onCancel={() => setView('details')} submitLabel={working ? 'Connecting...' : 'Connect mailbox'}
                                submitDisabled={working} />
                        </div>
                    </div> : selectedMailbox ? <>
                        <div className="mail-settings-account-header">
                            <span className={`mail-settings-provider-icon mail-settings-provider-large mail-settings-provider-${String(selectedMailbox.HOST || 'other').toLowerCase()}`}>
                                {providerInitial(selectedMailbox.HOST)}
                            </span>
                            <div className="mail-settings-account-title">
                                <h2>{selectedMailbox.EMAIL}</h2>
                                <p>{providerName(selectedMailbox.HOST)} · Owned by {Number(selectedMailbox.IS_OWNER) ? 'you' : selectedMailbox.OWNER_NAME || 'another user'}</p>
                            </div>
                            <span className="mail-settings-connected"><span aria-hidden="true" /> Configured</span>
                        </div>

                        <div className="mail-settings-account-actions">
                            <Link className="mail-settings-outline-button" to={constant.Authedcomponent.mailBox.url}
                                onClick={(event) => {
                                    if (!confirmDiscard()) {
                                        event.preventDefault();
                                        return;
                                    }
                                    setSessionStorage(constant.keys.mailBox, selectedMailbox.ID);
                                }}>
                                <i className="bx bx-envelope-open" aria-hidden="true" /> Open mailbox
                            </Link>
                            {Number(selectedMailbox.IS_OWNER) && selectedMailbox.HOST === 'microsoft' &&
                                <button type="button" className="mail-settings-outline-button"
                                    onClick={() => { if (confirmDiscard()) onMicrosoftSSO(); }}>
                                    <i className="bx bx-refresh" aria-hidden="true" /> Reconnect Microsoft
                                </button>}
                            {(Number(selectedMailbox.IS_OWNER) || Number(selectedMailbox.IS_ADMIN)) &&
                                <button type="button" className="mail-settings-remove-button" disabled={working || accessSaving}
                                    onClick={() => onRemove(selectedMailbox)}>
                                    <i className="bx bx-trash" aria-hidden="true" /> Remove mailbox
                                </button>}
                        </div>

                        {['outlook', 'hotmail'].includes(selectedMailbox.HOST) && <div className="mail-settings-legacy-note">
                            This password connection is no longer supported. Remove this mailbox and reconnect it with Microsoft 365.
                        </div>}

                        {Number(selectedMailbox.CAN_MANAGE_ACCESS) ?
                            <MailboxAccessPanel key={selectedMailbox.ID} mailbox={selectedMailbox}
                                onDirtyChange={setHasUnsavedChanges} onSavingChange={setAccessSaving}
                                onSaved={onSavedAccess} /> :
                            <div className="mail-settings-inherited mail-settings-shared-note">
                                This mailbox is shared with you. The owner or an admin manages access.
                            </div>}

                        {Number(selectedMailbox.IS_OWNER) && <div className="mail-settings-account-footer">
                            <details className="mail-settings-connection-details">
                                <summary>Connection details</summary>
                                <dl>
                                    <div><dt>Provider</dt><dd>{providerName(selectedMailbox.HOST)}</dd></div>
                                    {selectedMailbox.IMAP_HOST && <div><dt>IMAP server</dt><dd>{selectedMailbox.IMAP_HOST}</dd></div>}
                                    {selectedMailbox.IMAP_PORT && <div><dt>IMAP port</dt><dd>{selectedMailbox.IMAP_PORT}</dd></div>}
                                    {selectedMailbox.IMAP_HOST && <div><dt>IMAP TLS</dt><dd>{Number(selectedMailbox.IMAP_TLS) ? 'Enabled' : 'Disabled'}</dd></div>}
                                    {selectedMailbox.SMTP_HOST && <div><dt>SMTP server</dt><dd>{selectedMailbox.SMTP_HOST}</dd></div>}
                                    {selectedMailbox.SMTP_PORT && <div><dt>SMTP port</dt><dd>{selectedMailbox.SMTP_PORT}</dd></div>}
                                    {selectedMailbox.SMTP_HOST && <div><dt>SMTP security</dt><dd>{Number(selectedMailbox.SMTP_SECURE) ? 'Implicit SSL/TLS' : 'STARTTLS'}</dd></div>}
                                </dl>
                                <p>Credentials are hidden for security. To change a password connection, remove and reconnect the mailbox.</p>
                            </details>
                        </div>}
                    </> : <div className="mail-settings-empty-detail">
                        <span className="mail-settings-empty-icon"><i className="bx bx-envelope" aria-hidden="true" /></span>
                        <h2>No mailbox available</h2>
                        <p>Ask an admin for access to an existing mailbox, or connect a new address.</p>
                        <button type="button" className="mail-settings-primary-button" onClick={showAddForm}>Add mailbox</button>
                    </div>}
                </main>
            </div>
        </div>
    </Layout>;
};

export default Index;
